import { useEffect, useMemo, useState } from "react";
import { Box, Typography, OutlinedInput, FormControl, Button } from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import theme from "@/theme";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useGetUserByUidQuery } from "@/store/api/userApi";
import { useUpdateUserInfoMutation } from "@/store/api/authApi";

const ChangeUsername = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user: authUser } = useAppSelector((state) => state.auth);
  if (!authUser) return <Navigate to="/" replace />;
  const [updateUser] = useUpdateUserInfoMutation();

  const { data: user } = useGetUserByUidQuery(authUser.uid, {
    skip: !authUser?.uid,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [username, setUsername] = useState(location.state?.username || user?.username || "");

  useEffect(() => {
    if (user?.username) {
      setUsername(user.username);
    }
  }, [user?.username]);

  const validationError = useMemo(() => {
    if (!username) return "Please enter username";
    if (username.length < 4) return "User must be at least 4 characters";
    if (username.length > 30) return "Username must be at most 30 characters";
    if (!/^[a-zA-Z0-9._]+$/.test(username)) {
      return "Username can only contain letters, numbers, periods and underscores";
    }
    return null;
  }, [username]);

  const isValid = !validationError && username !== user?.username;

  const displayError =
    serverError || (username !== (user?.username ?? "") ? validationError : null);

  const handleSave = async () => {
    if (isLoading || !isValid) return;
    setServerError(null);
    setIsLoading(true);

    try {
      const res = updateUser({ uid: authUser.uid, data: { username } }).unwrap();

      if (!(await res).username) {
        setServerError("Failed to update username. Please try again.");
        return;
      }
      navigate("/settings/account/information", {
        state: {
          message: "The username has been successfully changed.",
          success: true,
        },
      });
    } catch (err: any) {
      const raw = err?.data?.message || err?.message || "Something went wrong";
      const errorMessage = Array.isArray(raw) ? raw[0] : raw;
      setServerError(
        typeof errorMessage === "string" && errorMessage.toLowerCase().includes("already exists")
          ? "The username is already taken."
          : errorMessage
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setServerError(null);
    setUsername(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  return (
    <>
      <SettingTitle title="Change username" submit={handleSave} back isActive={isValid} />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          px: "16px",
          py: "10px",
        }}
      >
        <input type="password" autoComplete="new-password" style={{ display: "none" }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "15px",
              lineHeight: "24px",
              color: "#292929",
              fontStyle: "Medium",
              letterSpacing: 0,
            }}
          >
            Username
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
            <FormControl fullWidth error={!!displayError}>
              <OutlinedInput
                value={username}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                type={"text"}
                error={!!displayError}
                sx={{
                  width: "100%",
                  height: "50px",
                  borderRadius: "999px",
                  maxWidth: "408px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E7E9" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C6CACE" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.custom.mainBlue,
                    borderWidth: "1px",
                  },

                  "& .MuiInputBase-input::placeholder": {
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: 0,
                    color: "#C6CACE",
                    opacity: 1,
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "15px",
                    lineHeight: "20px",
                    letterSpacing: "-0.02em",
                    color: "#383C40",
                    padding: "12px 16px",
                  },
                  ...(displayError && {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E62A3B",
                    },
                  }),
                }}
                autoComplete="off"
                name="changeUserame"
                id="changeUserame"
              />
              {displayError && (
                <Typography
                  sx={{
                    color: "#E62A3B",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "16px",
                    mt: 0.5,
                  }}
                >
                  {displayError}
                </Typography>
              )}
            </FormControl>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                bgcolor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                borderRadius: "28px",
                minWidth: "110px",
                height: "50px",
                color: "#fff",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                lineHeight: "22px",
                padding: "14px 24px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#8BC5F4",
                  color: "#FFFFFF",
                  opacity: 1,
                },
              }}
              disabled={!isValid}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ChangeUsername;
